"use strict";
import React, { useState } from "react";
import {
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Checkbox,
    ListItemText,
    FormControlLabel,
    Divider,
    Paper,
    Stack,
    Button
} from "@mui/material";

const benchmarkSuites = [
    "EmergentTTS-Eval",
    "VoiceMOS",
    "Blizzard",
    "NISQA"
];

const datasetSplits = ["Full", "Subset (10%)", "Custom"];

const ttsProviders = ["Incantor Voice", "ElevenLabs"];

const languages = [
    "English",
    "Spanish",
    "French",
    "German",
    "Japanese",
    "Mandarin",
    "Portuguese"
];

const scoringDimensions = [
    "Naturalness",
    "Emotion",
    "Pronunciation",
    "Prosody",
    "Intelligibility"
];

const outputMetrics = ["MOS", "Win-Rate vs Baseline", "Category Breakdown"];

const baselineOptions = [
    "None",
    "Incantor-Q3-VoiceMOS",
    "ElevenLabs-Launch-Baseline"
];

const VoiceBenchmarks = () => {
    const [runName, setRunName] = useState("");
    const [benchmarkSuite, setBenchmarkSuite] = useState(benchmarkSuites[0]);
    const [datasetSplit, setDatasetSplit] = useState(datasetSplits[0]);
    const [runDescription, setRunDescription] = useState("");
    const [ttsProvider, setTtsProvider] = useState(ttsProviders[0]);
    const [modelVariant, setModelVariant] = useState("");
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [selectedDimensions, setSelectedDimensions] = useState(scoringDimensions);
    const [selectedOutputMetrics, setSelectedOutputMetrics] = useState(outputMetrics);
    const [baselineSelection, setBaselineSelection] = useState(baselineOptions[0]);
    const [baselineFileName, setBaselineFileName] = useState("");
    const [notifyWhenComplete, setNotifyWhenComplete] = useState(true);

    const handleTextChange = (setter) => (event) => {
        setter(event.target.value);
    };

    const handleSelectChange = (setter) => (event) => {
        setter(event.target.value);
    };

    const handleFileChange = (event) => {
        const file = event.target.files && event.target.files[0];
        setBaselineFileName(file ? file.name : "");
    };

    const handleSubmit = (event) => {
        event.preventDefault();
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 960, mx: "auto" }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                Voice Benchmarks
            </Typography>

            <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: "var(--color-section-bg)" }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Run Info
                </Typography>
                <Stack spacing={2}>
                    <TextField
                        label="Run Name"
                        value={runName}
                        onChange={handleTextChange(setRunName)}
                        placeholder="Incantor-EN-Eval-Nov"
                        fullWidth
                        required
                    />
                    <FormControl fullWidth>
                        <InputLabel id="benchmark-suite-label">Benchmark Suite</InputLabel>
                        <Select
                            labelId="benchmark-suite-label"
                            value={benchmarkSuite}
                            label="Benchmark Suite"
                            onChange={handleSelectChange(setBenchmarkSuite)}
                        >
                            {benchmarkSuites.map((suite) => (
                                <MenuItem key={suite} value={suite}>
                                    {suite}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel id="dataset-split-label">Dataset Split</InputLabel>
                        <Select
                            labelId="dataset-split-label"
                            value={datasetSplit}
                            label="Dataset Split"
                            onChange={handleSelectChange(setDatasetSplit)}
                        >
                            {datasetSplits.map((split) => (
                                <MenuItem key={split} value={split}>
                                    {split}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Run Description"
                        value={runDescription}
                        onChange={handleTextChange(setRunDescription)}
                        placeholder="Notes on versions, configs, or objectives"
                        fullWidth
                        multiline
                        minRows={3}
                    />
                </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: "var(--color-section-bg)" }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Model Selection
                </Typography>
                <Stack spacing={2}>
                    <FormControl fullWidth>
                        <InputLabel id="tts-provider-label">TTS Provider</InputLabel>
                        <Select
                            labelId="tts-provider-label"
                            value={ttsProvider}
                            label="TTS Provider"
                            onChange={handleSelectChange(setTtsProvider)}
                        >
                            {ttsProviders.map((provider) => (
                                <MenuItem key={provider} value={provider}>
                                    {provider}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Model Variant / Voice"
                        value={modelVariant}
                        onChange={handleTextChange(setModelVariant)}
                        placeholder="Incantor v3 — Calm"
                        fullWidth
                    />
                    <FormControl fullWidth>
                        <InputLabel id="languages-label">Languages</InputLabel>
                        <Select
                            labelId="languages-label"
                            multiple
                            value={selectedLanguages}
                            onChange={handleSelectChange(setSelectedLanguages)}
                            input={<OutlinedInput label="Languages" />}
                            renderValue={(selected) => selected.join(", ")}
                        >
                            {languages.map((language) => (
                                <MenuItem key={language} value={language}>
                                    <Checkbox checked={selectedLanguages.indexOf(language) > -1} />
                                    <ListItemText primary={language} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: "var(--color-section-bg)" }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Evaluation Settings
                </Typography>
                <Stack spacing={2}>
                    <FormControl fullWidth>
                        <InputLabel id="scoring-dimensions-label">Scoring Dimensions</InputLabel>
                        <Select
                            labelId="scoring-dimensions-label"
                            multiple
                            value={selectedDimensions}
                            onChange={handleSelectChange(setSelectedDimensions)}
                            input={<OutlinedInput label="Scoring Dimensions" />}
                            renderValue={(selected) => selected.join(", ")}
                        >
                            {scoringDimensions.map((dimension) => (
                                <MenuItem key={dimension} value={dimension}>
                                    <Checkbox checked={selectedDimensions.indexOf(dimension) > -1} />
                                    <ListItemText primary={dimension} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel id="output-metrics-label">Output Metrics</InputLabel>
                        <Select
                            labelId="output-metrics-label"
                            multiple
                            value={selectedOutputMetrics}
                            onChange={handleSelectChange(setSelectedOutputMetrics)}
                            input={<OutlinedInput label="Output Metrics" />}
                            renderValue={(selected) => selected.join(", ")}
                        >
                            {outputMetrics.map((metric) => (
                                <MenuItem key={metric} value={metric}>
                                    <Checkbox checked={selectedOutputMetrics.indexOf(metric) > -1} />
                                    <ListItemText primary={metric} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Baseline for Comparison
                        </Typography>
                        <Stack spacing={1}>
                            <FormControl fullWidth>
                                <InputLabel id="baseline-selection-label">Select Baseline</InputLabel>
                                <Select
                                    labelId="baseline-selection-label"
                                    value={baselineSelection}
                                    label="Select Baseline"
                                    onChange={handleSelectChange(setBaselineSelection)}
                                >
                                    {baselineOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button variant="outlined" component="label">
                                Upload Baseline File
                                <input type="file" hidden onChange={handleFileChange} />
                            </Button>
                            {baselineFileName && (
                                <Typography variant="body2" color="text.secondary">
                                    Selected file: {baselineFileName}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: "var(--color-section-bg)" }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Notifications
                </Typography>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={notifyWhenComplete}
                            onChange={(event) => setNotifyWhenComplete(event.target.checked)}
                        />
                    }
                    label="Email When Complete"
                />
                <Typography variant="body2" color="text.secondary">
                    Receive a completion summary with run highlights directly in your inbox.
                </Typography>
            </Paper>

            <Divider sx={{ mb: 3 }} />

            <Button variant="contained" type="submit">
                Save & Run
            </Button>
        </Box>
    );
};

export default VoiceBenchmarks;
